import { useState, useEffect, useCallback } from 'react';
import { useLoyalty } from '../../../contexts/LoyaltyContext';
import { apiPost, apiGet } from '../../../lib/api';

interface QuickAction {
  type: string;
  label: string;
  icon: string;
  subtitle: string;
}

interface LoyaltyQuickActionsProps {
  actions?: QuickAction[];
  columns?: '2' | '3' | '4';
}

const DEFAULT_ACTIONS: QuickAction[] = [
  { type: 'checkin', label: 'Daily Check-in', icon: 'calendar', subtitle: '+50 Points' },
  { type: 'poll_complete', label: 'Weekly Poll', icon: 'poll', subtitle: 'Share your opinion' },
  { type: 'referral', label: 'Refer a Friend', icon: 'person_add', subtitle: '+500 Points each' },
];

const ICON_MAP: Record<string, string> = {
  calendar: '\uD83D\uDCC5',
  poll: '\uD83D\uDCCA',
  person_add: '\uD83D\uDC65',
  gift: '\uD83C\uDF81',
  star: '\u2B50',
  trophy: '\uD83C\uDFC6',
  heart: '\u2764\uFE0F',
  bolt: '\u26A1',
  check: '\u2705',
  target: '\uD83C\uDFAF',
};

interface CheckinStatus {
  checkedInToday: boolean;
  currentStreak: number;
}

export function LoyaltyQuickActionsBlock({ props }: { props: LoyaltyQuickActionsProps }) {
  const actions = props.actions?.length ? props.actions : DEFAULT_ACTIONS;
  const cols = props.columns ?? '3';
  const { data, refetch } = useLoyalty();
  const programId = data?.programId;

  const [checkinDone, setCheckinDone] = useState(false);
  const [loading, setLoading] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<{ type: string; message: string; isError?: boolean } | null>(null);

  // Check if already checked in today
  useEffect(() => {
    if (!programId) return;
    apiGet<CheckinStatus>(`/customer/loyalty/checkin/status?programId=${programId}`)
      .then((status) => {
        if (status.checkedInToday) setCheckinDone(true);
      })
      .catch(() => { /* ignore - not enrolled yet or no checkin table */ });
  }, [programId]);

  const showFeedback = useCallback((type: string, message: string, isError = false) => {
    setFeedback({ type, message, isError });
    setTimeout(() => setFeedback(null), 3000);
  }, []);

  const handleAction = useCallback(async (action: QuickAction) => {
    if (!programId) {
      showFeedback(action.type, 'Loyalty program not available', true);
      return;
    }

    setLoading(action.type);
    try {
      switch (action.type) {
        case 'checkin': {
          if (checkinDone) {
            showFeedback('checkin', 'Already checked in today!');
            break;
          }
          const result = await apiPost<{ pointsAwarded?: number; streak?: number }>('/customer/loyalty/checkin', { programId });
          setCheckinDone(true);
          const pts = result.pointsAwarded ?? 50;
          const streak = result.streak ?? 1;
          showFeedback('checkin', `+${pts} points! Streak: ${streak} days`);
          refetch();
          break;
        }
        case 'referral': {
          window.location.href = '/loyalty-hub#referral';
          break;
        }
        default: {
          // Track generic action via the track API
          await apiPost('/customer/loyalty/track', { programId, ruleType: action.type });
          showFeedback(action.type, 'Action completed!');
          refetch();
          break;
        }
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Action failed';
      if (msg.includes('already') || msg.includes('Already')) {
        setCheckinDone(true);
        showFeedback(action.type, 'Already completed today!');
      } else {
        showFeedback(action.type, msg, true);
      }
    } finally {
      setLoading(null);
    }
  }, [programId, checkinDone, refetch, showFeedback]);

  const gridCols: Record<string, string> = {
    '2': 'grid-cols-1 sm:grid-cols-2',
    '3': 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
    '4': 'grid-cols-2 sm:grid-cols-4',
  };

  return (
    <div className={`grid ${gridCols[cols] ?? gridCols['3']} gap-4`}>
      {actions.map((action, i) => {
        const isLoading = loading === action.type;
        const isCheckinDone = action.type === 'checkin' && checkinDone;
        const actionFeedback = feedback?.type === action.type ? feedback : null;

        return (
          <button
            key={action.type + i}
            type="button"
            disabled={isLoading || isCheckinDone}
            onClick={() => handleAction(action)}
            className={`loyalty-card p-5 text-left group cursor-pointer transition-all duration-200 hover:scale-[1.02] hover:shadow-lg hover:border-[var(--color-primary)] ${
              isCheckinDone ? 'opacity-60 cursor-not-allowed' : ''
            } ${isLoading ? 'animate-pulse' : ''}`}
          >
            <div className="text-2xl mb-3 transition-transform duration-200 group-hover:scale-110">
              {isCheckinDone ? '\u2705' : (ICON_MAP[action.icon] ?? action.icon)}
            </div>
            <h3 className="loyalty-heading text-sm font-bold text-[var(--color-text)] mb-1">
              {action.label}
            </h3>
            <p className="text-xs text-[var(--color-text-muted)]">
              {actionFeedback
                ? <span className={actionFeedback.isError ? 'text-red-400' : 'text-green-400'}>{actionFeedback.message}</span>
                : isCheckinDone
                  ? 'Done for today!'
                  : action.subtitle
              }
            </p>
          </button>
        );
      })}
    </div>
  );
}
